import type { MaterialKarte, MaterialMeldungKurz } from './materialTypes';

/**
 * Adresse und Kartendaten der Materialverwaltung — von Meldeseite, Server-Route
 * und Karten-PDF gemeinsam benutzt (docs/materialverwaltung-plan.md).
 *
 * Die Adresse `<origin>/m/<token>` steht auf gedruckten Karten, die lange im
 * Umlauf bleiben: ihre Form ist endgültig. Pfad statt Hash — kürzer, der QR
 * wird gröber, und ein späterer Android-App-Link kann am Pfad greifen.
 */

/** 16 Hex, klein — so vergibt Nr. 178 den Token. */
export const MATERIAL_TOKEN_MUSTER = /^[0-9a-f]{16}$/;

export const istMaterialToken = (x: unknown): x is string =>
    typeof x === 'string' && MATERIAL_TOKEN_MUSTER.test(x);

/** Adresse auf der Karte. Ein abschließender Schrägstrich am Origin fällt weg. */
export const materialKartenUrl = (origin: string, token: string): string =>
    `${origin.replace(/\/+$/, '')}/m/${token}`;

/** Token aus dem Pfad `/m/<token>` (Schrägstrich am Ende erlaubt), sonst null. */
export const materialTokenAusPfad = (pathname: string): string | null => {
    const treffer = /^\/m\/([0-9a-f]{16})\/?$/.exec(pathname);
    return treffer ? treffer[1] : null;
};

const text = (x: unknown): string | null =>
    typeof x === 'string' && x.trim() !== '' ? x : null;

const objekt = (x: unknown): Record<string, unknown> | null =>
    x !== null && typeof x === 'object' && !Array.isArray(x) ? x as Record<string, unknown> : null;

const meldungAusRpc = (roh: unknown): MaterialMeldungKurz | null | undefined => {
    if (roh === null || roh === undefined) return null;
    const m = objekt(roh);
    if (!m) return undefined;
    const gemeldetAm = text(m.gemeldet_am);
    const zuletzt = text(m.zuletzt_ausgeloest_am);
    const gesperrtBis = text(m.gesperrt_bis);
    const status = m.status;
    if (!gemeldetAm || !zuletzt || !gesperrtBis) return undefined;
    if (status !== 'offen' && status !== 'bestellt') return undefined;
    const anzahl = typeof m.anzahl === 'number' && Number.isFinite(m.anzahl) && m.anzahl >= 1 ? Math.floor(m.anzahl) : 1;
    return {
        gemeldetAm,
        zuletztAusgeloestAm: zuletzt,
        anzahl,
        melderKurz: text(m.melder_kurz),
        status,
        bestelltAm: text(m.bestellt_am),
        gesperrtBis,
    };
};

/**
 * JSON aus `material_karte_public` bzw. dem Kartenteil von `material_melden`
 * (Nr. 179, snake_case) → `MaterialKarte`. null bei null (Token unbekannt oder
 * deaktiviert) und bei jeder unlesbaren Form — die Seite zeigt dann „Karte
 * ungültig“, statt halbe Daten.
 *
 * Form: `{ artikel: { id, name, lagerort, hinweis }, firma,
 *          meldung: null | { gemeldet_am, zuletzt_ausgeloest_am, anzahl,
 *                            melder_kurz, status, bestellt_am, gesperrt_bis } }`
 */
export const karteAusRpc = (roh: unknown): MaterialKarte | null => {
    const k = objekt(roh);
    if (!k) return null;
    const a = objekt(k.artikel);
    if (!a) return null;
    const id = text(a.id);
    const name = text(a.name);
    if (!id || !name) return null;
    const meldung = meldungAusRpc(k.meldung);
    if (meldung === undefined) return null;
    return {
        artikel: { id, name, lagerort: text(a.lagerort), hinweis: text(a.hinweis) },
        firma: text(k.firma) ?? '',
        meldung,
    };
};
